import React from 'react';
import colors from 'src/style/colors';
import { mediaQueries } from 'src/style/breakpoints';

const certificationCard = {
  default: {
    title: 'Des formations certifiantes : titres RNCP',
    description:
      "Obtenez un titre RNCP reconnu par l'État, du niveau 5 (Bac +2) au niveau 6 (Bac +3/4). Une certification officielle, délivrée par le Ministère du Travail, qui renforce votre crédibilité auprès des recruteurs.",
  },
  'rncp6-fullTime': {
    title: 'Une formation certifiante : RNCP 6',
    description:
      "Obtenez un titre RNCP de niveau 6 (Bac +3/4) reconnu par l'État. Une certification officielle, délivrée par le Ministère du Travail, qui renforce votre crédibilité auprès des recruteurs.",
  },
  rncp5: {
    title: 'Une formation certifiante : RNCP 5',
    description:
      "Obtenez un titre RNCP de niveau 5 (Bac +2) reconnu par l'État. Une certification officielle, délivrée par le Ministère du Travail, qui renforce votre crédibilité auprès des recruteurs.",
  },
  'rncp5-e-learning': {
    title: 'Une formation certifiante : RNCP 5',
    description:
      "Obtenez un titre RNCP de niveau 5 (Bac +2) reconnu par l'État. Une certification officielle, délivrée par le Ministère du Travail, qui renforce votre crédibilité auprès des recruteurs.",
  },
  'rncp6-partTime': {
    title: 'Une formation certifiante : RNCP 6',
    description:
      "Obtenez un titre RNCP de niveau 6 (Bac +3/4) reconnu par l'État. Une certification officielle, délivrée par le Ministère du Travail, qui renforce votre crédibilité auprès des recruteurs.",
  },
  'rncp6-alternance': {
    title: 'Une formation certifiante : RNCP 6',
    description:
      "Obtenez un titre RNCP de niveau 6 (Bac +3/4) reconnu par l'État. Une certification officielle, délivrée par le Ministère du Travail, qui renforce votre crédibilité auprès des recruteurs.",
  },
};

const getCardData = (formationType, historicalCopy) => [
  {
    icon: '💰',
    title: 'Des options de financement adaptées à vos besoins',
    description:
      "Nos formations sont finançables via le CPF, France Travail, Transitions Pro et d'autres dispositifs. Nous vous accompagnons concrètement dans la constitution de votre dossier, étape par étape.",
  },
  {
    icon: '👨‍💻',
    title: 'Le soutien de nos formateurs, eux-mêmes développeurs',
    description: `Vous êtes accompagné·e par 4 formateurs et mentors en permanence, avec un ratio d'1 formateur pour 10 élèves. Présents toute la journée, ils assurent un suivi individuel, des réponses rapides et des échanges bienveillants, ${
      historicalCopy ? 'en présentiel comme à distance' : 'à distance'
    }.`,
  },
  {
    icon: '🎓',
    title: 'Une pédagogie unique, depuis 2016',
    description:
      "Nous avons déjà aidé plus de 2 500 personnes à changer de vie ! Notre méthode a fait ses preuves et continue d'évoluer pour rester alignée avec les usages et les attentes du marché.",
  },
  {
    icon: '💻',
    title: 'Des formations créées par des développeurs',
    description:
      "Le Reacteur a été fondé par Xavier Colombel (CEO) et Farid Safi (CTO), développeurs depuis plus de 20 ans. Leur expérience du terrain et de l'open source guide une pédagogie pensée par des développeurs, pour les développeurs.",
  },
  {
    icon: '🛠️',
    title: 'Un apprentissage par la pratique',
    description:
      'Vous travaillez sur de nombreux projets concrets, inspirés de cas réels (web et mobile). Reproduire, comprendre et construire : une méthode qui vous permet de créer un portfolio solide et professionnel.',
  },
  {
    icon: '🏆',
    ...(certificationCard[formationType] || certificationCard.default),
  },
];

const WhyJoinSection = ({ formationType, cards: cardsProp, historicalCopy = false }) => {
  const cards = cardsProp || getCardData(formationType, historicalCopy);
  return (
    <section
      css={{
        padding: '5rem 2rem',
        backgroundColor: colors.white,
        [mediaQueries.phoneOnly]: {
          padding: '3rem 1rem',
        },
      }}
    >
      <div
        css={{
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        {/* Section Title */}
        <h2
          css={{
            textAlign: 'center',
            fontSize: '2.5rem',
            marginBottom: '1rem',
            color: colors.purpleDark,
            fontWeight: 700,
            [mediaQueries.phoneOnly]: {
              fontSize: '1.75rem',
            },
          }}
        >
          Pourquoi rejoindre <span css={{ color: colors.purple }}>Le Reacteur ?</span>
        </h2>

        {/* Subtitle */}
        <p
          css={{
            textAlign: 'center',
            fontSize: '1.25rem',
            color: colors.darkGrey,
            marginBottom: '3rem',
            [mediaQueries.phoneOnly]: {
              fontSize: '1rem',
              marginBottom: '2rem',
            },
          }}
        >
          Formez-vous en toute confiance
        </p>

        {/* Cards Grid - 3 columns */}
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '2rem',
            '@media only screen and (max-width: 1024px)': {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
            [mediaQueries.phoneOnly]: {
              gridTemplateColumns: '1fr',
              gap: '1.5rem',
            },
          }}
        >
          {cards.map((card, index) => (
            <div
              key={index}
              css={{
                backgroundColor: colors.white,
                padding: '2rem',
                borderRadius: 16,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
                },
                [mediaQueries.phoneOnly]: {
                  padding: '1.5rem',
                },
              }}
            >
              {/* Card Icon */}
              <div
                css={{
                  width: 60,
                  height: 60,
                  background: `linear-gradient(135deg, ${colors.purple} 0%, ${colors.purpleLight} 100%)`,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.75rem',
                  marginBottom: '1.5rem',
                }}
              >
                {card.icon}
              </div>

              {/* Card Title */}
              <h3
                css={{
                  fontSize: '1.25rem',
                  marginBottom: '1rem',
                  color: colors.purpleDark,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  [mediaQueries.phoneOnly]: {
                    fontSize: '1.15rem',
                  },
                }}
              >
                {card.title}
              </h3>

              {/* Card Description */}
              <p
                css={{
                  color: colors.darkGrey,
                  lineHeight: 1.7,
                  margin: 0,
                  fontSize: '1rem',
                }}
              >
                {card.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyJoinSection;
